import React, { useState, useEffect, useCallback } from 'react';
import { RotateCcw, Trash2, Loader2, CalendarDays, Trophy, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ConfirmDeleteDialog } from '@/components/ConfirmDeleteDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const KIND_ICON = { stats_game: Trophy, stats_season: Layers, event: CalendarDays };

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('session_token')}` });

const TrashRow = ({ item, busy, onRestore, onForever }) => {
  const Icon = KIND_ICON[item.kind] || Trash2;
  return (
    <li className="flex items-center gap-3 py-3" data-testid={`trash-item-${item.id}`}>
      <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-slate-500" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-800 truncate">{item.name}</div>
        <div className="text-xs text-slate-500 truncate">
          {item.subtitle ? `${item.subtitle} · ` : ''}
          <span className="text-amber-600 font-medium">{item.days_left} day{item.days_left === 1 ? '' : 's'} left</span>
        </div>
      </div>
      <Button size="sm" variant="outline" onClick={() => onRestore(item)} disabled={busy} className="shrink-0"
        data-testid={`trash-restore-${item.id}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
        <span className="ml-1 hidden sm:inline">Restore</span>
      </Button>
      <button type="button" onClick={() => onForever(item)} disabled={busy}
        className="shrink-0 p-2 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
        title="Delete forever" data-testid={`trash-forever-${item.id}`}>
        <Trash2 className="w-4 h-4" />
      </button>
    </li>
  );
};

// "Recently deleted" list (7-day trash). group: 'stats' (games + seasons) or 'events' (schedules).
export const RecentlyDeletedDialog = ({ open, onClose, group, onRestored }) => {
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [foreverItem, setForeverItem] = useState(null);

  const load = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/trash?group=${group}`, { headers: authHeaders() });
    setItems(res.ok ? (await res.json()).items : []);
  }, [group]);

  useEffect(() => { if (open) { setItems(null); load(); } }, [open, load]);

  const restore = async (item) => {
    setBusyId(item.id);
    const res = await fetch(`${API_URL}/api/trash/${item.id}/restore`, { method: 'POST', headers: authHeaders() });
    const data = await res.json().catch(() => ({}));
    setBusyId(null);
    if (!res.ok) { toast.error(data.detail || 'Could not restore'); return; }
    toast.success(data.skipped_games
      ? `"${item.name}" restored (${data.skipped_games} game(s) skipped: a newer game exists for that match)`
      : `"${item.name}" restored`);
    setItems(prev => prev.filter(i => i.id !== item.id));
    onRestored?.(item);
  };

  const deleteForever = async () => {
    const item = foreverItem;
    const res = await fetch(`${API_URL}/api/trash/${item.id}`, { method: 'DELETE', headers: authHeaders() });
    if (!res.ok) { toast.error('Could not delete'); return; }
    toast.success(`"${item.name}" permanently deleted`);
    setItems(prev => prev.filter(i => i.id !== item.id));
    setForeverItem(null);
  };

  const label = group === 'events' ? 'schedules' : 'games and seasons';
  return (
    <>
      <Dialog open={open && !foreverItem} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-lg" data-testid={`recently-deleted-dialog-${group}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#00247D]">
              <Trash2 className="w-5 h-5" /> Recently deleted
            </DialogTitle>
            <DialogDescription>
              Deleted {label} stay here for 7 days, then they're erased automatically.
            </DialogDescription>
          </DialogHeader>
          {items === null ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : items.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-500" data-testid="recently-deleted-empty">Nothing here.</p>
          ) : (
            <ul className="divide-y divide-slate-100 max-h-[60vh] overflow-y-auto -mx-1 px-1">
              {items.map(item => (
                <TrashRow key={item.id} item={item} busy={busyId === item.id} onRestore={restore} onForever={setForeverItem} />
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDeleteDialog
        open={!!foreverItem}
        title="Delete forever?"
        itemName={foreverItem?.name}
        description="will be permanently erased right now. This cannot be undone."
        confirmLabel="Delete Forever"
        onClose={() => setForeverItem(null)}
        onConfirm={deleteForever}
        testId="trash-forever"
      />
    </>
  );
};

export default RecentlyDeletedDialog;
